import React, { useState, useEffect, useCallback } from 'react';
import { getOnlineUsers } from '../../services/gameService';
import { getCurrentRank } from '../../utils/gameData';

interface OnlineUser {
  _id: string;
  username: string;
  rankId: number;
  location: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface OnlineUsersData {
  success: boolean;
  onlineUsers: OnlineUser[];
  totalOnline: number;
  lastUpdated: string;
}

const UsersOnline: React.FC = () => {
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [totalOnline, setTotalOnline] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data: OnlineUsersData = await getOnlineUsers();
      
      if (data.success) {
        setOnlineUsers(data.onlineUsers);
        setTotalOnline(data.totalOnline);
      } else {
        setError('Failed to fetch online users');
      }
    } catch (err) {
      console.error('Error fetching online users:', err);
      setError('Failed to load online users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOnlineUsers();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchOnlineUsers();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchOnlineUsers]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Alive':
        return 'var(--success-color)';
      case 'Dead':
        return 'var(--error-color)';
      case 'Jailed':
        return 'var(--warning-color)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getProfileImage = (username: string, rankId: number) => {
    // Generate a consistent color based on username
    const colors = [
      '#ff6b35', '#f7931e', '#ffd23f', '#4caf50', '#2196f3', 
      '#9c27b0', '#e91e63', '#ff5722', '#795548', '#607d8b'
    ];
    const colorIndex = username.charCodeAt(0) % colors.length;
    const backgroundColor = colors[colorIndex];
    
    return {
      backgroundColor,
      initials: username.substring(0, 2).toUpperCase()
    };
  };

  if (loading && onlineUsers.length === 0) {
    return (
      <div className="users-online-container">
        <div className="loading-container">
          <div className="loading-card">
            <h3>Loading online users...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="users-online-container">
      {error && (
        <div className="error-notification">
          <p>{error}</p>
          <button className="btn btn-sm btn-primary" onClick={fetchOnlineUsers}>
            Try Again
          </button>
        </div>
      )}

      <div className="users-online-list">
        {onlineUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👻</div>
            <h3>No users online</h3>
            <p>Be the first to join the streets!</p>
          </div>
        ) : (
          <>
            <div className="users-grid-compact">
              {onlineUsers.map((user) => {
                const profileImage = getProfileImage(user.username, user.rankId);
                const rank = getCurrentRank(user.rankId);
                
                return (
                  <div key={user._id} className="user-card-compact">
                    <div className="user-image-container">
                      <div 
                        className="user-profile-image" 
                        style={{ backgroundColor: profileImage.backgroundColor }}
                      >
                        {profileImage.initials}
                      </div>
                      <div 
                        className="status-indicator"
                        style={{ backgroundColor: getStatusColor(user.status) }}
                      ></div>
                    </div>
                    <div className="user-content">
                      <div className="user-name">{user.username}</div>
                      <div className="user-rank">{rank?.name || 'Unknown'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="users-total">
              <div className="total-badge">
                {totalOnline} {totalOnline === 1 ? 'User' : 'Users'} Online
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UsersOnline;
